import { useCallback, useEffect, useState } from 'react';
import {
  autonomousPlanningApi,
  type AgentPerformance,
  type AutonomousDashboard,
  type AutonomousPlanningSummary,
  type CollaborationGraph,
  type DigitalEmployee,
  type ExecutionReadiness,
  type LearningRecord,
} from '../../api/autonomousplanning';

type Tab = 'center' | 'employees' | 'goals' | 'workflow' | 'approvals' | 'readiness' | 'learning' | 'performance';
type Theme = 'light' | 'dark';

function priorityColor(p: string) {
  if (p === 'critical') return 'text-red-500';
  if (p === 'high') return 'text-orange-500';
  if (p === 'medium') return 'text-amber-500';
  return 'text-slate-500';
}

export function AutonomousPlanningPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('center');
  const [dashboard, setDashboard] = useState<AutonomousDashboard | null>(null);
  const [summary, setSummary] = useState<AutonomousPlanningSummary | null>(null);
  const [employees, setEmployees] = useState<DigitalEmployee[]>([]);
  const [readiness, setReadiness] = useState<ExecutionReadiness | null>(null);
  const [learning, setLearning] = useState<LearningRecord[]>([]);
  const [graph, setGraph] = useState<CollaborationGraph | null>(null);
  const [performance, setPerformance] = useState<AgentPerformance[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, sum, emps, ready, lr, g, perf] = await Promise.all([
        autonomousPlanningApi.dashboard(),
        autonomousPlanningApi.summary(),
        autonomousPlanningApi.listDigitalEmployees(),
        autonomousPlanningApi.executionReadiness(),
        autonomousPlanningApi.learning(),
        autonomousPlanningApi.collaborationGraph(),
        autonomousPlanningApi.agentPerformance(),
      ]);
      setDashboard(dash);
      setSummary(sum);
      setEmployees(emps);
      setReadiness(ready);
      setLearning(lr);
      setGraph(g);
      setPerformance(perf);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load autonomous planning');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleApprove = async (taskId: string, goalId: string) => {
    setActionMsg(null);
    try {
      await autonomousPlanningApi.processApproval({ task_id: taskId, goal_id: goalId, action: 'approve', comment: 'Approved in Autonomous Planning Center' });
      setActionMsg('Task approved');
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approval failed');
    }
  };

  const handleReject = async (taskId: string, goalId: string) => {
    setActionMsg(null);
    try {
      await autonomousPlanningApi.processApproval({ task_id: taskId, goal_id: goalId, action: 'reject', reason: 'Requires additional validation' });
      setActionMsg('Task rejected');
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Rejection failed');
    }
  };

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'center', label: 'Autonomous Center' },
    { id: 'employees', label: 'Digital Employees' },
    { id: 'goals', label: 'Goal Manager' },
    { id: 'workflow', label: 'Workflow Visualizer' },
    { id: 'approvals', label: 'Approval Center' },
    { id: 'readiness', label: 'Execution Readiness' },
    { id: 'learning', label: 'Learning Dashboard' },
    { id: 'performance', label: 'Agent Performance' },
  ];

  const pendingTasks = summary?.tasks.filter((t) => t.requires_approval && t.approval_status === 'requested') ?? [];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise Autonomous Planning</h1>
            <p className={`mt-1 text-sm ${muted}`}>
              Digital Employees, goal-driven automation, agent orchestration, and human-supervised enterprise planning
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
            <button type="button" onClick={refresh} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700">Refresh</button>
          </div>
        </div>
        {dashboard && summary && (
          <p className={`mt-2 text-sm ${muted}`}>
            Autonomy Score {dashboard.planning_autonomy_score.toFixed(0)} · {dashboard.active_digital_employees} Digital Employees · Cycle {summary.active_cycle} ({summary.cycle_status.replace(/_/g, ' ')})
            {summary.human_governance_enabled && ' · Human governance active'}
          </p>
        )}
      </div>

      <div className="border-b px-6">
        <nav className="-mb-px flex flex-wrap gap-4 overflow-x-auto" aria-label="Autonomous planning tabs">
          {tabs.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${tab === t.id ? 'border-violet-500 text-violet-600' : `border-transparent ${muted}`}`}>{t.label}</button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {error && <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {actionMsg && <div className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{actionMsg}</div>}

        {tab === 'center' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Planning Autonomy Score', value: dashboard.planning_autonomy_score.toFixed(0) },
              { label: 'Automation Score', value: dashboard.planning_automation_score.toFixed(0) },
              { label: 'Agent Productivity', value: `${dashboard.agent_productivity.toFixed(0)}%` },
              { label: 'Workforce Utilization', value: `${dashboard.workforce_utilization.toFixed(0)}%` },
              { label: 'Autonomous Planning Rate', value: `${dashboard.autonomous_planning_rate.toFixed(0)}%` },
              { label: 'Execution Readiness', value: dashboard.execution_readiness_score.toFixed(0) },
              { label: 'Cycle Reduction', value: `${dashboard.planning_cycle_reduction_pct.toFixed(0)}%` },
              { label: 'Planning ROI', value: `${dashboard.planning_roi.toFixed(1)}x` },
            ].map((kpi) => (
              <div key={kpi.label} className={card}>
                <p className={`text-xs uppercase tracking-wide ${muted}`}>{kpi.label}</p>
                <p className="mt-2 text-2xl font-semibold">{kpi.value}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'employees' && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {employees.map((e) => (
              <div key={e.id} className={card}>
                <h3 className="font-medium">{e.name}</h3>
                <p className={`text-xs uppercase ${muted}`}>{e.type.replace(/_/g, ' ')}</p>
                <p className={`mt-2 text-sm ${muted}`}>{e.mission}</p>
                <p className="mt-2 text-sm">Productivity {(e.productivity_score * 100).toFixed(0)}% · Health {e.health_score.toFixed(0)}</p>
                <p className={`mt-1 text-xs ${muted}`}>Authority: {e.authority_limits}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'goals' && summary && (
          <div className="space-y-3">
            {summary.goals.map((g) => (
              <div key={g.id} className={card}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <span className={`text-xs font-semibold uppercase ${priorityColor(g.priority)}`}>{g.priority}</span>
                    <h3 className="mt-1 font-medium">{g.title}</h3>
                    <p className={`mt-1 text-sm ${muted}`}>{g.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold">{g.progress_pct.toFixed(0)}%</p>
                    <p className={`text-xs ${muted}`}>{g.target_metric}: {g.current_value} → {g.target_value}</p>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {g.assigned_agents.map((a) => (
                    <span key={a} className={`rounded-full px-2 py-0.5 text-xs ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}>{a.replace(/_/g, ' ')}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'workflow' && graph && summary && (
          <div className="space-y-4">
            <div className={card}>
              <h2 className="text-lg font-semibold">Agent Collaboration Graph</h2>
              <p className={`mt-1 text-sm ${muted}`}>{graph.nodes.length} nodes · {graph.edges.length} collaboration edges</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {graph.nodes.map((n) => (
                  <div key={n.id} className={`rounded-lg p-3 ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className="font-medium">{n.label}</p>
                    <p className={`text-xs ${muted}`}>{n.node_type} · {n.status}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className={card}>
              <h3 className="font-medium">Active Tasks</h3>
              <ul className="mt-2 space-y-2">
                {summary.tasks.map((t) => (
                  <li key={t.id} className={`text-sm ${muted}`}>
                    <span className="font-medium text-inherit">{t.title}</span> — {t.assigned_employee.replace(/_/g, ' ')} ({t.status})
                    <p className="text-xs">{t.reasoning_summary}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {tab === 'approvals' && (
          <div className="space-y-3">
            {pendingTasks.length === 0 && <p className={muted}>No tasks pending approval.</p>}
            {pendingTasks.map((t) => (
              <div key={t.id} className={card}>
                <h3 className="font-medium">{t.title}</h3>
                <p className={`mt-1 text-sm ${muted}`}>{t.reasoning_summary}</p>
                <p className={`text-xs ${muted}`}>Confidence {(t.confidence * 100).toFixed(0)}% · Requires human approval</p>
                <div className="mt-3 flex gap-2">
                  <button type="button" onClick={() => handleApprove(t.id, t.goal_id)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white">Approve</button>
                  <button type="button" onClick={() => handleReject(t.id, t.goal_id)} className="rounded-lg bg-red-600 px-3 py-1.5 text-sm text-white">Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'readiness' && readiness && (
          <div className={card}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Execution Readiness Index</h2>
              <span className={`rounded-full px-3 py-1 text-sm font-medium ${readiness.ready ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {readiness.ready ? 'Ready' : 'Not Ready'}
              </span>
            </div>
            <p className="mt-2 text-3xl font-semibold">{readiness.overall_score.toFixed(0)}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { label: 'Data Completeness', value: readiness.data_completeness },
                { label: 'Constraint Compliance', value: readiness.constraint_compliance },
                { label: 'Policy Compliance', value: readiness.policy_compliance },
                { label: 'Integration Health', value: readiness.integration_health },
              ].map((c) => (
                <div key={c.label} className={`rounded-lg p-3 ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <p className={`text-xs ${muted}`}>{c.label}</p>
                  <p className="font-medium">{c.value.toFixed(0)}%</p>
                </div>
              ))}
            </div>
            {readiness.blockers.length > 0 && (
              <div className="mt-4">
                <h3 className="font-medium text-amber-600">Blockers</h3>
                <ul className="mt-1 list-disc pl-5 text-sm">{readiness.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
              </div>
            )}
          </div>
        )}

        {tab === 'learning' && (
          <div className="space-y-3">
            {learning.map((l) => (
              <div key={l.id} className={card}>
                <p className="font-medium">{l.feedback_type.replace(/_/g, ' ')} · {l.employee_type?.replace(/_/g, ' ')}</p>
                {l.business_outcome && <p className={`mt-1 text-sm ${muted}`}>{l.business_outcome}</p>}
                {l.improvement_action && <p className="mt-1 text-sm text-violet-600">{l.improvement_action}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === 'performance' && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {performance.map((p) => (
              <div key={p.employee_type} className={card}>
                <h3 className="font-medium">{p.employee_name}</h3>
                <p className={`text-sm ${muted}`}>{p.tasks_completed} tasks · Success {p.success_rate.toFixed(0)}%</p>
                <p className="mt-1 text-sm">Confidence {(p.avg_confidence * 100).toFixed(0)}% · Approval {p.approval_rate.toFixed(0)}%</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
